// Query options shared by the screens that read the data and by lib/prefetch.ts, which starts the
// same requests early (at boot, on a tap or hover, while idle). Sharing the key, the request and
// the staleTime is what lets a screen pick up a prefetched answer instead of asking again.
//
// Each factory takes the response type from its caller, so the types stay with the screens.
// staleTime: how long an answer is reused without asking the server again when a screen mounts
// (moving between tabs). Money-related ones stay short and every mutation still invalidates them;
// catalog-like data (walls, gift cards, config, leaderboards) can wait longer.
import type { QueryClient } from '@tanstack/react-query';
import type { FaqGroup } from '@gaintplay/core/faq';
import { api, earlyFetch } from './api';
import { androidVersion } from './device';
import { faqFromJson } from '../pages/info/faq-markup';
import { getLocale } from '../i18n';

const SECOND = 1000;

/**
 * `path` in the reader's language, for answers cached at the edge by address that carry text
 * staff wrote (translated by the API): ?lang=xx, nothing for English. src/boot.ts asks the same.
 */
const inLanguage = (path: string) => {
  const locale = getLocale();
  return locale === 'en' ? path : `${path}${path.includes('?') ? '&' : '?'}lang=${locale}`;
};

export const statsQuery = <T>() => ({
  queryKey: ['stats', getLocale()] as const,
  queryFn: () => api<T>(inLanguage('/public/stats')),
  staleTime: 60 * SECOND,
});

/** Whether GaintPlay is open where the visitor is (Admin → Countries), for components/CountryGate. */
export const countryQuery = {
  queryKey: ['country'] as const,
  queryFn: () =>
    api<{ country: string | null; access: 'open' | 'no_signups' | 'blocked' }>('/public/country'),
  staleTime: 5 * 60 * SECOND,
};

/** Recent earnings for the landing page and Earn's live panel (the realtime Worker's snapshot). */
export const feedSnapshotQuery = <T extends { items: unknown[] }>() => ({
  queryKey: ['feed-snapshot'] as const,
  queryFn: async (): Promise<T> => {
    const r = await earlyFetch('/rt/feed.json');
    return r.ok ? ((await r.json()) as T) : ({ items: [] } as unknown as T);
  },
  staleTime: 15 * SECOND,
});

/**
 * The published FAQ (/faq), checked, in the reader's language (the API's translation of staff's
 * answers; English where one isn't in yet). The language is in the address because the edge keeps
 * one copy per address. Edits in the admin take a few minutes to arrive anyway.
 */
export const faqQuery = () => ({
  queryKey: ['faq', getLocale()] as const,
  queryFn: async (): Promise<{ groups: FaqGroup[] }> => {
    const groups = faqFromJson((await api<{ groups?: unknown }>(inLanguage('/public/faq'))).groups);
    if (!groups) throw new Error('The FAQ came back in a shape this page can’t read.');
    return { groups };
  },
  staleTime: 5 * 60 * SECOND,
});

/**
 * The guide's closing video in the reader's language (Admin → Settings → New-member guide): Bunny
 * links for phones and desktop, null where a language has none. Cached at the edge by address.
 */
export const guideQuery = () => ({
  queryKey: ['guide', getLocale()] as const,
  queryFn: () =>
    api<{ video: { phone: string | null; desktop: string | null } | null }>(
      inLanguage('/public/guide'),
    ),
  staleTime: 5 * 60 * SECOND,
});

export const leaderboardQuery = <T>(period: string) => ({
  queryKey: ['leaderboard', period] as const,
  queryFn: () => api<T>(`/rewards/leaderboard?period=${period}`),
  staleTime: 60 * SECOND,
});

export const authConfigQuery = <T>() => ({
  queryKey: ['auth-config'] as const,
  queryFn: () => api<T>('/auth/config'),
  staleTime: Infinity,
});

export const dailyQuery = <T>() => ({
  queryKey: ['daily'] as const,
  queryFn: () => api<T>('/rewards/daily'),
  staleTime: 60 * SECOND,
});

export const wallsQuery = <T>() => ({
  queryKey: ['walls', getLocale()] as const,
  queryFn: () => api<T>(inLanguage('/walls')),
  staleTime: 60 * SECOND,
});

/** What the gated walls ask of this member (confirmed email, coins earned); read only when a wall has a gate. */
export const wallStatusQuery = <T>() => ({
  queryKey: ['walls-status'] as const,
  queryFn: () => api<T>('/walls/status'),
  staleTime: 30 * SECOND,
});

/** Offers read for this member from the networks' offers APIs (slow the first time: seconds). */
export const offersQuery = <T>() => ({
  queryKey: ['offers', getLocale()] as const,
  queryFn: async () => {
    const os = await androidVersion();
    return api<T>(os ? `/offers?os=${os}` : '/offers');
  },
  staleTime: 60 * SECOND,
});

/** The offers this member started in the last 60 days, with what each one paid so far. */
export const myOffersQuery = <T>() => ({
  queryKey: ['my-offers', getLocale()] as const,
  queryFn: () => api<T>('/offers/mine'),
  staleTime: 30 * SECOND,
});

export const walletQuery = <T>() => ({
  queryKey: ['wallet'] as const,
  queryFn: () => api<T>('/me/wallet'),
  staleTime: 15 * SECOND,
});

export const withdrawalsQuery = <T>() => ({
  queryKey: ['withdrawals'] as const,
  queryFn: () => api<T>('/cashout/withdrawals'),
  staleTime: 15 * SECOND,
});

export const legacyQuery = <T>() => ({
  queryKey: ['legacy'] as const,
  queryFn: () => api<T>('/me/legacy'),
  staleTime: 60 * SECOND,
});

/** The one-off bonuses (Android app, browser notifications) and the community links. */
export interface RewardExtras {
  /** checkable: collected in the app after a Play Integrity check (false: not set up). */
  appBonus: { coins: number; claimed: boolean; checkable: boolean };
  notifyBonus: { coins: number; claimed: boolean };
  community: {
    discord: string | null;
    telegram: string | null;
    x: string | null;
    reddit: string | null;
    youtube: string | null;
  };
}
export const extrasQuery = () => ({
  queryKey: ['reward-extras'] as const,
  queryFn: () => api<RewardExtras>('/rewards/extras'),
  staleTime: 60 * SECOND,
});

export const referralsQuery = <T>() => ({
  queryKey: ['referrals'] as const,
  queryFn: () => api<T>('/rewards/referrals'),
  staleTime: 30 * SECOND,
});

export const ticketsQuery = <T>() => ({
  queryKey: ['tickets'] as const,
  queryFn: () => api<T>('/support'),
  staleTime: 15 * SECOND,
});

export const ticketQuery = <T>(id: string | undefined) => ({
  queryKey: ['ticket', id] as const,
  queryFn: () => api<T>(`/support/${id}`),
  staleTime: 5 * SECOND,
});

export const cashoutConfigQuery = <T>() => ({
  queryKey: ['cashout-config'] as const,
  queryFn: () => api<T>('/cashout/config'),
  staleTime: 30 * SECOND,
});

/** One page of the gift card catalog. Desktop shows it alone; the app appends pages through the
 *  same cache (pages/Cashout.tsx). */
export const catalogQuery = <T>(
  country: string | null,
  search: string,
  category: string | null,
  page: number,
) => {
  const params = new URLSearchParams();
  if (country) params.set('country', country);
  if (search) params.set('q', search);
  if (category) params.set('category', category);
  if (page) params.set('page', String(page));
  return {
    queryKey: ['gift-catalog', country, search, category, page] as const,
    queryFn: () => api<T>(`/cashout/catalog?${params}`),
    staleTime: 120 * SECOND,
  };
};

/** The app's catalog list: "Show more" appends pages. Each page is the same request and cache entry
 *  desktop uses (catalogQuery), so a prefetched next page lands instantly. */
export const catalogFeedQuery = <T extends { pageSize: number; total: number }>(
  qc: QueryClient,
  country: string | null,
  search: string,
  category: string | null,
) => ({
  queryKey: ['gift-catalog-feed', country, search, category] as const,
  // Retries happen here, once; the inner fetch retrying too would make a failure take 4 tries.
  queryFn: ({ pageParam }: { pageParam: number }) =>
    qc.query({ ...catalogQuery<T>(country, search, category, pageParam), retry: false }),
  initialPageParam: 0,
  getNextPageParam: (last: T, _all: T[], lastParam: number) =>
    (lastParam + 1) * last.pageSize < last.total ? lastParam + 1 : undefined,
  staleTime: 120 * SECOND,
});
